// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import BusComponent from '../BusComponent';
import Trigger from 'rc-trigger';
import { focusFirstChild } from '../../helpers/Util';
import LocationAnalysisReports from './LocationAnalysisReports';
import { HelpTourTargets } from '../../enums/HelpTourDefinitions';

const popupAlignOptions = {
    points: ['tc', 'bc'],
    offset: [-120, 10],
};

/**
 * @typedef Props
 * @property {string} mapInstanceId
 * @property {import('react-intl').intlShape} intl
 *
 * @typedef State
 * @property {Array.<import('../../types').FeaturedSurveyReport | import('../../types').FeaturedOlapReport>} featuredReports
 * @property {boolean} isUserStarterTier
 * @property {import('../../objects/ReportLimits').default | null} reportLimits
 * @property {boolean} popupVisible
 *
 * @extends {BusComponent<Props, State>}
 */
class LocationAnalysisReportPopup extends BusComponent {
    constructor(props, context) {
        super(props, context);
        /** @type {State} */
        this.state = {
            featuredReports: [],
            isUserStarterTier: false,
            popupVisible: false,
            reportLimits: null,
        };
        this.boundAfterPopupRender = this.afterPopupRender.bind(this);
    }

    componentDidMount() {
        this.bindGluBusEvents({
            CUSTOM_MAP_SELECTION_FEATURED_REPORTS: this.onFeaturedReportsResponse,
            USER_INFO_GET_SUCCESS: this.onUserInfo,
            REPORT_LIMITS_GET_SUCCESS: this.onReportLimitsResponse,
            REPORT_LIMITS_LOAD_SUCCESS: this.onReportLimitsLoadResponse,
        });

        this.emit('CUSTOM_MAP_SELECTION_FEATURED_REPORTS_REQUEST');
        this.emit('USER_INFO_GET_REQUEST', { source: this });
        this.emit('REPORT_LIMITS_GET_REQUEST');
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    onUserInfo = (userInfo, source) => {
        if (source !== this) return;
        this.setState({
            isUserStarterTier: userInfo && userInfo.isStarterTierUser,
        });
    };

    onReportLimitsResponse = reportLimits => {
        this.setState({
            reportLimits,
        });
    };

    onReportLimitsLoadResponse = ({ reportLimits }) => {
        this.setState({
            reportLimits,
        });
    };

    /** @param {Array.<import('../../types').FeaturedSurveyReport | import('../../types').FeaturedOlapReport>} featuredReports */
    onFeaturedReportsResponse = featuredReports => {
        this.setState({ featuredReports });
    };

    afterPopupRender = visible => {
        if (visible) {
            focusFirstChild(this.popupContainer);
        }
    };

    handlePopupVisibleChange = popupVisible => {
        this.emit('RESET_DOWNLOAD_REPORT_STATE');
        if (popupVisible) {
            this.emit('REPORT_LIMITS_GET_REQUEST');
        }
        this.setState({ popupVisible });
    };

    closePopup = () => {
        this.setState({ popupVisible: false });
    };

    render() {
        const { featuredReports, isUserStarterTier, reportLimits } = this.state;

        return (
            <Trigger
                action={['click']}
                popupVisible={this.state.popupVisible}
                onPopupVisibleChange={this.handlePopupVisibleChange}
                onPopupAlign={this.boundAfterPopupRender}
                popup={
                    <div
                        className="menu-popup"
                        ref={e => {
                            this.popupContainer = e;
                        }}
                    >
                        <div className="report-popup">
                            <div className="report-popup__header">
                                <span>
                                    {this.props.intl.formatMessage({
                                        id: 'dataBrowser.dataExports',
                                    })}
                                </span>
                                <button
                                    className="btn-icon radius-selector__modal-close"
                                    onClick={this.closePopup}
                                >
                                    <i className="material-icons">close</i>
                                </button>
                            </div>

                            {reportLimits && (
                                <div className="report-popup__info">
                                    <span>{reportLimits.remaining} remaining</span>
                                    <button
                                        onClick={() => this.emit('REPORT_LIMITS_POPUP_REQUEST')}
                                    >
                                        <i className="material-symbols">info</i>
                                    </button>
                                </div>
                            )}

                            <div className="report-popup__reports">
                                <LocationAnalysisReports
                                    mapInstanceId={this.props.mapInstanceId}
                                    reports={featuredReports}
                                    isUserStarterTier={isUserStarterTier}
                                />
                            </div>
                        </div>
                    </div>
                }
                popupAlign={popupAlignOptions}
            >
                <div data-tourId={HelpTourTargets.INFO_PANEL_EXPORT_DATA}>
                    <button data-testid="data-export-button" className="btn-chip btn-chip--insight-panel btn-chip--export" aria-haspopup>
                        <i className="material-symbols btn-chip__icon">download</i>
                        <span>{this.props.intl.formatMessage({ id: 'export' })}</span>
                    </button>
                </div>
            </Trigger>
        );
    }
}

LocationAnalysisReportPopup.propTypes = {
    mapInstanceId: PropTypes.string.isRequired,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(LocationAnalysisReportPopup);
